import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PortalError, papi } from './api';
import { fmtDate, usePortalText } from './text';
import { pArea, pPrimary, pSecondary } from './PortalApp';

// Etapa 2.3 · Estados de cuenta en el portal (solo aprobadores): revisar, aceptar o pedir aclaración.

interface Row { projectId: string; projectCode: string; projectName: string; month: string; model: string; status: string; dueAt: string | null; closedAt: string | null }
interface Detail {
  statement: {
    projectId: string; projectCode: string; projectName: string; organizationName: string; month: string; model: 'bag' | 'baseline' | 'on_demand';
    status: string; dueAt: string | null; closedAt: string | null; closedVia: 'client' | 'auto' | 'agent' | null; acceptedBy: string | null; canDecide: boolean;
    clarifications: { id: string; ticket: string | null; comment: string; by: string; at: string | null; resolvedAt: string | null; resolution: string | null }[];
  };
  summary: {
    charged: number; adjustmentsHours: number;
    bag: { maxHours: number; used: number; remaining: number } | null;
    baseline: { row: { available: number; used: number; inBand: number; outOfBand: number; carryOut: number } | null } | null;
  };
  lines: { ticket: string; subject: string; type: string | null; hours: number }[];
  adjustments: { kind: string; hours: number; ticket: string | null; reason: string }[];
}

const MONTHS = {
  es: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
};
const monthName = (lang: 'es' | 'en', m: string) => { const [y, mm] = m.split('-').map(Number); const n = MONTHS[lang][(mm ?? 1) - 1]!; return `${lang === 'es' ? n[0]!.toUpperCase() + n.slice(1) : n} ${y}`; };
const tone = (status: string) => status === 'sent' ? 'bg-[#FFF1DE] text-[#8A4B00] ring-[#FFD8A3]'
  : status === 'in_clarification' ? 'bg-[#E8F0F5] text-asx-navy ring-[#CFDEE8]' : 'bg-[#EEF1F2] text-[#5B6B73] ring-[#DCE3E6]';
const h = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

export function PortalStatementList() {
  const { slug = '' } = useParams();
  const { t, lang } = usePortalText();
  const q = useQuery({ queryKey: ['portal-statements'], queryFn: () => papi<{ statements: Row[] }>('/statements') });
  const rows = q.data?.statements ?? [];
  return (
    <div className="flex flex-col gap-5">
      <div>
        <Link to={`/t/${slug}`} className="text-sm text-[#3D6B8A] hover:underline">{t.back}</Link>
        <h1 className="mt-3 text-2xl font-semibold">{t.statements}</h1>
        <p className="mt-1 max-w-2xl text-sm text-[#5B6B73]">{t.statementsIntro}</p>
      </div>
      {q.isLoading ? <p role="status" className="py-10 text-center text-sm text-[#5B6B73]">{t.loading}</p>
        : rows.length === 0 ? <p className="rounded-xl border border-[#E1E7E9] bg-white px-6 py-14 text-center text-sm text-[#5B6B73]">{t.noStatements}</p> : (
          <ul className="flex flex-col divide-y divide-[#E1E7E9] overflow-hidden rounded-xl border border-[#E1E7E9] bg-white">
            {rows.map(r => (
              <li key={`${r.projectId}-${r.month}`}>
                <Link to={`/t/${slug}/estados/${r.projectId}/${r.month}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 hover:bg-[#F7F9F9]">
                  <span>
                    <span className="block font-semibold">{monthName(lang, r.month)}</span>
                    <span className="text-sm text-[#5B6B73]">{r.projectCode} · {r.projectName}</span>
                  </span>
                  <span className="flex items-center gap-3">
                    {r.status === 'sent' && r.dueAt && <span className="hidden text-xs text-[#5B6B73] sm:inline">{fmtDate(lang, r.dueAt)}</span>}
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${tone(r.status)}`}>{t.statementStatus[r.status] ?? r.status}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
    </div>
  );
}

export function PortalStatement() {
  const { slug = '', projectId = '', month = '' } = useParams();
  const { t, lang } = usePortalText();
  const queryClient = useQueryClient();
  const key = ['portal-statement', projectId, month];
  const q = useQuery({ queryKey: key, queryFn: () => papi<Detail>(`/statements/${projectId}/${month}`) });
  const [clarify, setClarify] = useState<{ ticket: string | null } | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const act = async (path: string, body?: unknown) => {
    setBusy(true); setError('');
    try {
      queryClient.setQueryData(key, await papi<Detail>(`/statements/${projectId}/${month}/${path}`, { method: 'POST', body: JSON.stringify(body ?? {}) }));
      void queryClient.invalidateQueries({ queryKey: ['portal-statements'] });
      setClarify(null); setComment('');
    } catch (err) { setError(err instanceof PortalError ? err.message : t.genericError); } finally { setBusy(false); }
  };

  if (q.isLoading) return <p role="status" className="py-16 text-center text-sm text-[#5B6B73]">{t.loading}</p>;
  if (q.isError || !q.data) return <p role="alert" className="rounded-xl bg-white p-8 text-center text-sm">{(q.error as Error)?.message ?? t.genericError}</p>;
  const { statement: s, summary, lines, adjustments } = q.data;
  const row = summary.baseline?.row;
  const canAct = s.canDecide || s.status === 'in_clarification';

  return (
    <div className="flex flex-col gap-5">
      <div>
        <Link to={`/t/${slug}/estados`} className="text-sm text-[#3D6B8A] hover:underline print:hidden">{t.back}</Link>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm text-[#5B6B73]">{s.projectCode} · {s.organizationName}</span>
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${tone(s.status)}`}>{t.statementStatus[s.status] ?? s.status}</span>
        </div>
        <h1 className="mt-1.5 text-2xl font-semibold">{t.statement} · {monthName(lang, s.month)}</h1>
        <p className="mt-1 text-sm text-[#5B6B73]">{s.projectName}</p>
      </div>
      {error && <p role="alert" className="rounded-lg bg-[#FDECEC] px-3 py-2 text-sm text-[#8F1D1D]">{error}</p>}

      <section className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-[#E1E7E9] bg-white p-5">
          <p className="text-sm text-[#5B6B73]">{t.stmtCharged}</p>
          <p className="mt-1 text-3xl font-semibold tabular-nums">{h(summary.charged)} h</p>
          {summary.adjustmentsHours !== 0 && <p className="mt-1 text-xs text-[#5B6B73]">{t.stmtAdjustments}: {summary.adjustmentsHours > 0 ? '+' : ''}{h(summary.adjustmentsHours)} h</p>}
        </div>
        <div className="rounded-xl border border-[#E1E7E9] bg-white p-5 text-sm">
          {summary.bag && <>
            <p className="font-semibold">{t.stmtBagUsed(summary.bag.used, summary.bag.maxHours)}</p>
            <p className="mt-1 text-[#5B6B73]">{t.stmtBagLeft(summary.bag.remaining)}</p>
          </>}
          {row && <>
            <p className="font-semibold">{t.stmtBaseline(row.used, row.available)}</p>
            {row.inBand > 0 && <p className="mt-1 text-[#8A4B00]">{t.stmtInBand(row.inBand)}</p>}
            {row.outOfBand > 0 && <p className="mt-1 text-[#8F1D1D]">{t.stmtOutOfBand(row.outOfBand)}</p>}
            {row.carryOut > 0 && <p className="mt-1 text-[#5B6B73]">{t.stmtRollover(row.carryOut)}</p>}
          </>}
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-[#E1E7E9] bg-white">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[#5B6B73]">
              <th scope="col" className="border-b border-[#E1E7E9] px-5 py-3 font-semibold">{t.stmtTicket}</th>
              <th scope="col" className="w-24 border-b border-[#E1E7E9] px-3 py-3 text-right font-semibold">{t.stmtHours}</th>
              {canAct && <th scope="col" className="w-24 border-b border-[#E1E7E9] px-3 py-3 print:hidden"><span className="sr-only">{t.stmtClarifyLine}</span></th>}
            </tr>
          </thead>
          <tbody>
            {lines.map(l => (
              <tr key={l.ticket || 'none'} className="border-b border-[#EEF1F2] last:border-0">
                <td className="px-5 py-3">
                  {l.ticket ? <span className="font-mono text-xs text-[#5B6B73]">{l.ticket}</span> : null}
                  <span className="block">{l.ticket ? l.subject : t.stmtNoTicket}</span>
                </td>
                <td className="px-3 py-3 text-right font-semibold tabular-nums">{h(l.hours)}</td>
                {canAct && (
                  <td className="px-3 py-3 text-right print:hidden">
                    <button type="button" className="text-xs text-[#3D6B8A] underline" onClick={() => { setClarify({ ticket: l.ticket || null }); setComment(''); }}>{t.stmtClarifyLine}</button>
                  </td>
                )}
              </tr>
            ))}
            {adjustments.map((a, i) => (
              <tr key={`adj-${i}`} className="border-b border-[#EEF1F2] bg-[#F7F9F9] last:border-0">
                <td className="px-5 py-3"><span className="text-xs font-semibold">{t.adjustmentKind[a.kind] ?? a.kind}{a.ticket ? ` · ${a.ticket}` : ''}</span><span className="block text-xs text-[#5B6B73]">{a.reason}</span></td>
                <td className="px-3 py-3 text-right tabular-nums">{a.hours > 0 && a.kind !== 'courtesy' && a.kind !== 'warranty' ? '+' : '−'}{h(Math.abs(a.hours))}</td>
                {canAct && <td className="print:hidden" />}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {s.clarifications.length > 0 && (
        <section className="flex flex-col gap-2">
          {s.clarifications.map(c => (
            <div key={c.id} className="rounded-xl border border-[#E1E7E9] bg-white p-4 text-sm">
              <p className="text-xs text-[#5B6B73]">{c.ticket ?? t.statements} · {fmtDate(lang, c.at, true)}</p>
              <p className="mt-1">“{c.comment}”</p>
              <p className={`mt-2 text-xs ${c.resolvedAt ? 'text-[#2E5A12]' : 'text-[#8A4B00]'}`}>{c.resolvedAt ? `${t.stmtAnswer}: ${c.resolution}` : t.stmtPending}</p>
            </div>
          ))}
        </section>
      )}

      {clarify ? (
        <section className="rounded-xl border border-[#FFD8A3] bg-[#FFFBF5] p-5 print:hidden">
          <label className="text-sm font-medium">{t.stmtClarifyWhat}{clarify.ticket ? ` (${clarify.ticket})` : ''}
            <textarea rows={3} className={`${pArea} mt-1.5`} value={comment} onChange={e => setComment(e.target.value)} autoFocus />
          </label>
          <div className="mt-3 flex gap-2">
            <button type="button" className={pPrimary} disabled={busy || comment.trim().length < 5} onClick={() => void act('clarify', { ticket: clarify.ticket, comment })}>{t.confirmAction}</button>
            <button type="button" className={pSecondary} onClick={() => setClarify(null)}>{t.cancel}</button>
          </div>
        </section>
      ) : (
        <section className="flex flex-wrap items-center gap-3 print:hidden">
          {s.canDecide && <button type="button" className={pPrimary} disabled={busy} onClick={() => void act('accept')}>{t.stmtAccept}</button>}
          {canAct && <button type="button" className={pSecondary} onClick={() => { setClarify({ ticket: null }); setComment(''); }}>{t.stmtClarify}</button>}
          <button type="button" className={pSecondary} onClick={() => window.print()}>{t.stmtPrint}</button>
          {s.canDecide && s.dueAt && <span className="text-xs text-[#5B6B73]">{t.stmtDue(fmtDate(lang, s.dueAt))}</span>}
        </section>
      )}
      {s.status === 'closed' && (
        <p className="rounded-lg bg-[#EEF1F2] px-4 py-3 text-sm text-[#3E4A50]">
          {s.closedVia === 'client' && s.acceptedBy ? t.stmtAccepted(s.acceptedBy.replace(/\s*\(.*\)$/, '')) : s.closedVia === 'auto' ? t.stmtAutoAccepted : t.stmtClosedByTeam} · {fmtDate(lang, s.closedAt)}
        </p>
      )}
    </div>
  );
}
